import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Fab, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Snackbar, Typography, IconButton, Tooltip } from '@material-ui/core';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import CircularProgress from '@material-ui/core/CircularProgress';
import SupportTicketService from 'services/SupportTicketService';
import useUser from 'hooks/useUser';
import MaterialSymbol from 'components/MaterialSymbol/MaterialSymbol';

const useStyles = makeStyles((theme) => ({
  fab: {
    position: 'fixed',
    bottom: theme.spacing(4),
    right: theme.spacing(4),
    zIndex: 9999,
    backgroundColor: '#111827',
    color: '#ffffff',
    '&:hover': {
      backgroundColor: '#1F2937',
    },
  },
  fabIcon: {
    color: '#ffffff !important',
    fontSize: '20px !important',
    fontVariationSettings: '"FILL" 0, "wght" 100, "GRAD" 0, "opsz" 24 !important',
  },
  dialog: {
    position: 'fixed',
    bottom: theme.spacing(12),
    right: theme.spacing(4),
    margin: 0,
    width: '400px',
    maxWidth: '100%',
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500],
  },
}));

export default function SupportTicketWidget() {
  const isWidgetEnabled = process.env.REACT_APP_ENABLE_SUPPORT_TICKETS_WIDGET === 'true';

  if (!isWidgetEnabled) {
    return null;
  }

  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [user] = useUser();

  const validationSchema = Yup.object({
    subject: Yup.string().required('Subject is required'),
    message: Yup.string().required('Message is required'),
  });

  const formik = useFormik({
    initialValues: {
      subject: '',
      message: '',
    },
    validationSchema: validationSchema,
    onSubmit: async (values, { resetForm }) => {
      setLoading(true);
      try {
        const email = user?.email || user?.userName || '';
        const firstName = user?.firstName || user?.name || '';
        const lastName = user?.lastName || '';
        const dealerId = user?.dealerId || user?.dealer_id || null;

        const payload = {
          subject: values.subject,
          message: values.message,
          email,
          firstName,
          lastName,
          dealerId
        };

        await SupportTicketService.createTicket(payload);
        
        setSnackbarMessage('Ticket created successfully!');
        setSnackbarOpen(true);
        resetForm();
        setOpen(false);
      } catch (error) {
        console.error(error);
        const errorMsg = error.response?.data?.error || 'Failed to create ticket. Please try again.';
        setSnackbarMessage(errorMsg);
        setSnackbarOpen(true);
      } finally {
        setLoading(false);
      }
    },
  });

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    formik.resetForm();
  };

  return (
    <>
      <Tooltip title="Support Tickets">
        <Fab className={`${classes.fab} shadcn-support-ticket-fab`} onClick={handleOpen} aria-label="support">
          <MaterialSymbol className={`${classes.fabIcon} shadcn-support-ticket-icon`}>support_agent</MaterialSymbol>
        </Fab>
      </Tooltip>

      <Dialog
        open={open}
        onClose={handleClose}
        classes={{ paper: classes.dialog }}
        hideBackdrop
        disableEnforceFocus
        style={{ pointerEvents: open ? 'auto' : 'none' }}
      >
        <DialogTitle>
          <Typography variant="h6">Contact Support</Typography>
          <IconButton aria-label="close" className={classes.closeButton} onClick={handleClose}>
            <MaterialSymbol>close</MaterialSymbol>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Create a ticket and our team will get back to you soon.
          </Typography>
          <form onSubmit={formik.handleSubmit}>
            <TextField
              autoFocus
              margin="dense"
              name="subject"
              label="Subject"
              type="text"
              fullWidth
              variant="outlined"
              value={formik.values.subject}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.subject && Boolean(formik.errors.subject)}
              helperText={formik.touched.subject && formik.errors.subject}
              disabled={loading}
            />
            <TextField
              margin="dense"
              name="message"
              label="Message"
              type="text"
              fullWidth
              multiline
              rows={4}
              variant="outlined"
              value={formik.values.message}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.message && Boolean(formik.errors.message)}
              helperText={formik.touched.message && formik.errors.message}
              disabled={loading}
            />
          </form>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="default" disabled={loading}>
            Cancel
          </Button>
          <Button onClick={formik.handleSubmit} color="primary" variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </>
  );
}
